import { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { ArrowLeft, Phone, Clock, MapPin, FileText, Building2, Flame, Star, TrendingUp, ExternalLink, CheckCircle2 } from "lucide-react";
import Header from "@/components/Header";
import PermitTrendChart from "@/components/PermitTrendChart";
import AhjIntelCard from "@/components/AhjIntelCard";
import AhjPlaybook from "@/components/AhjPlaybook";
import AhjContactsBadge from "@/components/AhjContactsBadge";
import api from "@/api/client";
import { useFavorites } from "@/hooks/useFavorites";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { toast } from "sonner";

export default function CountyDetail() {
  const { fips } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [trend, setTrend] = useState(null);
  const [monthly, setMonthly] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const { isFavorite, toggle } = useFavorites();

  useEffect(() => {
    Promise.all([
      api.get(`/counties/${fips}/permits`),
      api.get(`/counties/${fips}/trend`),
    ])
      .then(([permitsRes, trendRes]) => {
        setData(permitsRes.data);
        setTrend(trendRes.data.trend || []);
        setLoading(false);
      })
      .catch((e) => {
        setError(e.response?.data?.detail || "County not found");
        setLoading(false);
      });
  }, [fips]);

  const loadMonthly = async () => {
    if (monthly) return;
    const { data: m } = await api.get(`/counties/${fips}/trend/monthly`);
    setMonthly(m.monthly || []);
  };

  const handleFav = async () => {
    if (!data) return;
    try {
      const added = await toggle(data.county);
      toast.success(added ? "Added to favorites" : "Removed from favorites");
    } catch {
      toast.error("Could not update favorites");
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#F8FAFC]">
        <Header />
        <div className="max-w-5xl mx-auto px-6 py-12 space-y-6">
          <Skeleton className="h-8 w-40" />
          <Skeleton className="h-12 w-2/3" />
          <div className="grid grid-cols-4 gap-4">
            {[1, 2, 3, 4].map((i) => <Skeleton key={i} className="h-24" />)}
          </div>
          <Skeleton className="h-64" />
        </div>
      </div>
    );
  }

  if (error || !data) {
    return (
      <div className="min-h-screen bg-[#F8FAFC]">
        <Header />
        <div className="max-w-3xl mx-auto px-6 py-24 text-center" data-testid="county-error">
          <h2 className="font-outfit text-3xl font-semibold text-slate-900 mb-3">County not found</h2>
          <p className="text-slate-600 mb-6">{error}</p>
          <Button onClick={() => navigate("/map")} className="bg-slate-900 hover:bg-slate-800 text-white">
            <ArrowLeft className="w-4 h-4 mr-2" /> Back to map
          </Button>
        </div>
      </div>
    );
  }

  const { county, permit_categories, schedule_source, schedule_url, has_curated_data, tier, ahj_intel } = data;
  const permits = county.permits_2023 || {};
  const fav = isFavorite(county.fips);

  return (
    <div className="min-h-screen bg-[#F8FAFC]" data-testid="county-detail-page">
      <Header />
      <main className="max-w-5xl mx-auto px-6 py-12">
        <Link
          to="/map"
          data-testid="back-to-map-link"
          className="inline-flex items-center gap-1.5 text-sm text-slate-500 hover:text-slate-900 transition-colors mb-8"
        >
          <ArrowLeft className="w-4 h-4" /> Back to map
        </Link>

        <div className="mb-10 flex items-start justify-between gap-4 flex-wrap">
          <div>
            <div className="text-xs font-mono uppercase tracking-[0.2em] text-emerald-700 mb-3 flex items-center gap-1.5">
              <MapPin className="w-3 h-3" /> {county.state_name}
            </div>
            <h1 className="font-outfit text-4xl md:text-5xl font-bold tracking-tight text-slate-900" data-testid="county-detail-name">
              {county.full_name}
            </h1>
            <div className="mt-3 flex items-center gap-2 flex-wrap">
              <Badge variant="outline" className="font-mono">FIPS {county.fips}</Badge>
              <Badge variant="outline" className="font-mono">{county.state_abbr}</Badge>
              <Badge variant="outline" className="font-mono">Census BPS 2023</Badge>
              <AhjContactsBadge ahjKey={county.fips} scope="county" />
            </div>
          </div>
          <Button
            onClick={handleFav}
            variant="outline"
            data-testid="detail-favorite-btn"
            className={`h-11 ${fav ? "bg-amber-50 border-amber-300 text-amber-700 hover:bg-amber-100 hover:text-amber-800" : ""}`}
          >
            <Star className="w-4 h-4 mr-2" fill={fav ? "currentColor" : "none"} />
            {fav ? "Saved" : "Save county"}
          </Button>
        </div>

        {/* Real Census stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-12">
          <StatCard label="Population (2023)" value={(county.population || 0).toLocaleString()} />
          <StatCard label="Permits issued (2023)" value={(permits.total_bldgs || 0).toLocaleString()} accent />
          <StatCard label="Housing units permitted" value={(permits.total_units || 0).toLocaleString()} />
          <StatCard label="Per 1k residents" value={(county.permits_per_1k || 0).toFixed(2)} accent />
        </div>

        {/* Trend chart */}
        {trend && trend.length > 0 && (
          <section className="mb-12" data-testid="trend-section">
            <PermitTrendChart trend={trend} monthly={monthly} onLoadMonthly={loadMonthly} />
          </section>
        )}

        {/* AHJ Intel (from user's real spreadsheet) */}
        {ahj_intel && <AhjPlaybook ahjKey={fips} scope="county" jurisdictionFallback={ahj_intel.jurisdiction} />}

        {/* Permit breakdown */}
        <section className="mb-12" data-testid="permit-breakdown-section">
          <div className="text-xs font-mono uppercase tracking-[0.2em] text-slate-500 mb-3">2023 permit breakdown</div>
          <div className="bg-white border border-slate-200 rounded-xl p-6 grid grid-cols-1 md:grid-cols-3 gap-6">
            <BreakdownStat
              icon={<Building2 className="w-4 h-4" />}
              label="Single-family units"
              value={(permits.single_family_units || 0).toLocaleString()}
              hint="1-unit dwellings"
            />
            <BreakdownStat
              icon={<Building2 className="w-4 h-4" />}
              label="Multi-family units"
              value={(permits.multi_family_units || 0).toLocaleString()}
              hint="2+ unit structures"
            />
            <BreakdownStat
              icon={<TrendingUp className="w-4 h-4" />}
              label="Total construction value"
              value={`$${((permits.total_value || 0) / 1_000_000).toFixed(1)}M`}
              hint="Permitted value (2023)"
            />
          </div>
          <div className="text-[11px] text-slate-500 mt-2 italic">
            Source: U.S. Census Bureau — Building Permits Survey (BPS) Annual County file, 2023.
          </div>
        </section>

        {/* Permit categories */}
        <section data-testid="permits-section">
          <div className="flex items-baseline justify-between mb-3 flex-wrap gap-2">
            <div className="text-xs font-mono uppercase tracking-[0.2em] text-slate-500">Permit categories</div>
            {has_curated_data ? (
              <a
                href={schedule_url}
                target="_blank" rel="noopener noreferrer"
                data-testid="curated-source-link"
                className="text-[11px] font-mono text-emerald-700 hover:underline uppercase tracking-[0.18em] flex items-center gap-1"
              >
                <CheckCircle2 className="w-3 h-3" /> Live fees from {schedule_source}
                <ExternalLink className="w-3 h-3" />
              </a>
            ) : tier === "calibrated" ? (
              <a
                href={schedule_url || "#"}
                target="_blank" rel="noopener noreferrer"
                data-testid="calibrated-source-link"
                className="text-[11px] font-mono text-amber-700 hover:underline uppercase tracking-[0.18em] flex items-center gap-1"
              >
                Calibrated estimate · official site
                <ExternalLink className="w-3 h-3" />
              </a>
            ) : (
              <span className="text-[11px] font-mono text-slate-400 uppercase tracking-[0.18em]">Typical US ranges</span>
            )}
          </div>
          <div className="space-y-3">
            {permit_categories.map((p) => (
              <article
                key={p.type}
                data-testid={`permit-card-${p.type.replace(/\s+/g, '-').toLowerCase()}`}
                className="bg-white border border-slate-200 rounded-xl p-6 hover:border-slate-400 transition-colors"
              >
                <div className="flex items-start justify-between gap-6 flex-wrap">
                  <div className="flex-1 min-w-[260px]">
                    <div className="flex items-center gap-2 mb-2">
                      <div className="w-8 h-8 rounded-md bg-slate-900 text-white flex items-center justify-center">
                        <FileText className="w-4 h-4" />
                      </div>
                      <h4 className="font-outfit text-lg font-semibold text-slate-900">{p.type}</h4>
                      {p.source === "curated" && (
                        <Badge className="bg-emerald-50 text-emerald-700 border-emerald-200 font-mono text-[10px]" data-testid={`curated-badge-${p.type.replace(/\s+/g, '-').toLowerCase()}`}>
                          <CheckCircle2 className="w-2.5 h-2.5 mr-1" /> Curated
                        </Badge>
                      )}
                      {p.source === "calibrated" && (
                        <Badge className="bg-amber-50 text-amber-700 border-amber-200 font-mono text-[10px]" data-testid={`calibrated-badge-${p.type.replace(/\s+/g, '-').toLowerCase()}`}>
                          Calibrated
                        </Badge>
                      )}
                    </div>
                    <div className="text-xs font-mono uppercase tracking-[0.18em] text-slate-500 mb-2 flex items-center gap-1.5">
                      <Building2 className="w-3 h-3" /> {p.department}
                    </div>
                    <p className="text-sm text-slate-600 leading-relaxed mt-2">{p.description}</p>
                  </div>
                  <div className="grid grid-cols-2 gap-3 min-w-[240px]">
                    <MiniStat label="Typical fee" value={p.typical_fee_range} />
                    <MiniStat label="Processing" value={`${p.typical_processing_days} days`} />
                  </div>
                </div>
                <div className="mt-4 pt-4 border-t border-slate-100 text-sm text-slate-600 flex items-center gap-3 flex-wrap">
                  <span className="flex items-center gap-1.5"><Phone className="w-3.5 h-3.5 text-slate-400" />{p.contact_phone}</span>
                  <span className="flex items-center gap-1.5"><Clock className="w-3.5 h-3.5 text-slate-400" />Mon-Fri, 8am–5pm</span>
                </div>
              </article>
            ))}
          </div>
          <div className="mt-6 rounded-md border border-amber-200 bg-amber-50 p-4 text-xs text-amber-900 leading-relaxed flex gap-3">
            <Flame className="w-4 h-4 flex-none mt-0.5" />
            <div>
              <strong>Note:</strong> Permit <strong>volume</strong>, <strong>population</strong>, <strong>construction value</strong>, and the 10-year <strong>trend</strong> are real U.S. Census Bureau data (BPS 2014–2023).
              {has_curated_data
                ? <> Fees & processing days for this county are sourced from the linked official schedule above.</>
                : <> Fee ranges & processing days shown below are <em>typical U.S. ranges</em> — a county-specific curated schedule is not yet wired for this FIPS.</>}
            </div>
          </div>
        </section>
      </main>
    </div>
  );
}

function StatCard({ label, value, accent }) {
  return (
    <div className={`border rounded-xl p-5 ${accent ? "bg-slate-900 border-slate-900 text-white" : "bg-white border-slate-200"}`}>
      <div className={`text-[10px] font-mono uppercase tracking-[0.18em] mb-2 ${accent ? "text-emerald-300" : "text-slate-500"}`}>{label}</div>
      <div className={`font-outfit text-2xl font-bold ${accent ? "text-white" : "text-slate-900"}`}>{value}</div>
    </div>
  );
}

function BreakdownStat({ icon, label, value, hint }) {
  return (
    <div>
      <div className="flex items-center gap-1.5 text-[10px] font-mono uppercase tracking-[0.18em] text-slate-500 mb-2">
        {icon} {label}
      </div>
      <div className="font-outfit text-2xl font-bold text-slate-900">{value}</div>
      <div className="text-xs text-slate-500 mt-1">{hint}</div>
    </div>
  );
}

function MiniStat({ label, value }) {
  return (
    <div className="border border-slate-200 rounded-md p-3 bg-slate-50/50">
      <div className="text-[10px] font-mono uppercase tracking-[0.18em] text-slate-500 mb-1">{label}</div>
      <div className="text-sm font-medium text-slate-900">{value}</div>
    </div>
  );
}
